/**
 * A rule drawn as blocks - the read-only half of the block editor (roadmap E2).
 *
 * Scratch's grammar in Explorer's clothes: a condition is a hexagon, a value is
 * round, a group nests under a hairline bracket, and the clauses - Ships?,
 * Conditions, How long?, Severity, Variables - join into one stack. Colour is one
 * hue per kind of piece, spent as an outline and a pale tint, never a fill.
 *
 * It draws the version 2 tree (`backend/argus_tree/`), which every rule has:
 * the server lifts version 1 rules before sending them. Nothing here evaluates
 * anything (`docs/plans/argus.md` rule 3) - it only shows what a rule says.
 *
 * An operator this file has no words for is still drawn, by its name with its
 * arguments inside, so a new catalogue entry never makes a rule disappear.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { ArgusRuleTree, ArgusTreeDocument, ArgusTreeNode } from '../../api/types.ts';
import { foldGate, formulaOf, groupKind, negated, signalKeys, type GroupKind } from './argus-tree-edit.ts';

const COMPARE_WORDS: Record<string, string> = {
  '>': 'above',
  '>=': 'at least',
  '<': 'below',
  '<=': 'at most',
  '==': 'equals',
  '!=': 'is not',
};

const WINDOW_WORDS: Record<string, string> = {
  avg_over: 'average',
  min_over: 'lowest',
  max_over: 'highest',
  sum_over: 'total',
  p95_over: '95th percentile',
  stddev_over: 'spread',
  change_over: 'change',
  missing_over: 'missing readings',
  share_over: '% of time',
};

const ARITHMETIC = new Set(['+', '-', '*', '/']);

/** Functions over values at each moment, in words: never the raw "max". */
export const FUNCTION_WORDS: Record<string, string> = { max: 'highest of', min: 'lowest of', abs: 'size of' };

/** Operators whose answer is a condition - drawn as hexagons. */
const CONDITIONS = new Set([
  ...Object.keys(COMPARE_WORDS),
  'between',
  'outside',
  'and',
  'or',
  'not',
  'at_least',
  'valid',
  'stale',
]);

const UNIT_WORDS: Record<string, string> = { m: 'minute', h: 'hour', d: 'day' };

/** `"30m"` -> `"30 minutes"`. Anything else is shown as it was written. */
export function durationWords(text: string): string {
  const match = /^(\d+)([mhd])$/.exec(text);
  if (!match) return text;
  const count = Number(match[1]);
  const unit = UNIT_WORDS[match[2] as string] as string;
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

function numberWord(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(6)));
}

function hex(kind: string, body: TemplateResult, not = false): TemplateResult {
  return html`<span class="argus-b-hex is-${kind} ${not ? 'is-not' : ''}"
    ><span class="argus-b-in">${not ? html`<span class="argus-b-not">not</span>` : nothing}${body}</span></span
  >`;
}

/**
 * How a group reads, shared with the editor. Plain heads, chosen 2026-10-08
 * (`docs/design/sketches/argus-rule-groups.html`, B): two items read as people
 * say it - BOTH, EITHER, NEITHER, NOT BOTH - and the head says what it asks
 * ("of these 4 are true"), not only its kind. Inside NONE the join is "nor".
 */
export function groupWords(
  kind: GroupKind,
  count: number,
): { head: string; tail: string; join: string | null; add: string } {
  const two = count === 2;
  const verb = kind === 'any' || kind === 'none' ? 'is' : 'are';
  const tail =
    count === 0 ? 'of these' : count === 1 ? 'of this is true' : two ? `of these ${verb} true` : `of these ${count} ${verb} true`;
  switch (kind) {
    case 'any':
      return { head: two ? 'EITHER' : 'AT LEAST ONE', tail, join: 'or', add: '+ or' };
    case 'none':
      return { head: count === 1 ? 'NOT' : two ? 'NEITHER' : 'NONE', tail: count === 1 ? 'this is true' : tail, join: 'nor', add: '+ or' };
    case 'not_all':
      return { head: two ? 'NOT BOTH' : 'NOT ALL', tail, join: 'and', add: '+ and' };
    case 'at_least':
      return { head: 'AT LEAST', tail: `of these ${count} are true`, join: null, add: '+ condition' };
    default:
      return { head: two ? 'BOTH' : 'ALL', tail, join: 'and', add: '+ and' };
  }
}

/**
 * The colour key under the Conditions hint: only what this rule uses - the
 * join words, and "must be false" once something is negated. Nothing for a
 * rule of one plain condition.
 */
export function logicKey(node: unknown): TemplateResult | typeof nothing {
  const used = new Set<string>();
  const walk = (n: unknown): void => {
    if (typeof n !== 'object' || n === null || !('op' in n)) return;
    const neg = negated(n);
    if (neg) {
      used.add('not');
      walk(neg.inner);
      return;
    }
    const kind = groupKind(n);
    const args = (n as unknown as { args: unknown[] }).args;
    const items = kind === 'none' || kind === 'not_all' ? ((args[0] as { args?: unknown[] })?.args ?? []) : args;
    if (kind === 'none' || kind === 'not_all') used.add('not');
    if (kind && items.length > 1) {
      const join = groupWords(kind, items.length).join;
      if (join) used.add(join);
    }
    items.forEach(walk);
  };
  walk(node);
  if (!used.size) return nothing;
  return html`<div class="argus-b-key" aria-label="Colour key">
    ${used.has('and') ? html`<span><span class="argus-b-join is-key is-all">and</span>every one</span>` : nothing}
    ${used.has('or') ? html`<span><span class="argus-b-join is-key is-any">or</span>one is enough</span>` : nothing}
    ${used.has('nor') ? html`<span><span class="argus-b-join is-key is-none">nor</span>not one</span>` : nothing}
    <span><span class="argus-b-mini"></span>must be true</span>
    ${used.has('not') ? html`<span><span class="argus-b-mini is-not"></span>must be false</span>` : nothing}
  </div>`;
}

function round(body: TemplateResult): TemplateResult {
  return html`<span class="argus-b-round">${body}</span>`;
}

function word(text: string): TemplateResult {
  return html`<span class="argus-b-word">${text}</span>`;
}

function renderArg(arg: ArgusTreeNode | string | number, doc: ArgusTreeDocument): TemplateResult {
  if (typeof arg === 'string') return html`<span class="argus-b-pill is-time">${durationWords(arg)}</span>`;
  if (typeof arg === 'number') return html`<span class="argus-b-pill is-num">${arg}</span>`;
  return renderNode(arg, doc);
}

/** One node of the tree as blocks. Exported for the tests. */
/** `root`: the clause's own group, drawn as a bare rail rather than a box. */
export function renderNode(node: ArgusTreeNode, doc: ArgusTreeDocument, root = false, not = false): TemplateResult {
  // A condition that must be false: the condition in red, starting with NOT, no box.
  const neg = negated(node);
  if (neg) return renderNode(neg.inner, doc, false, true);
  if ('input' in node) {
    const entry = doc.inputs[node.input];
    const exceptions = Object.keys(entry?.per_entity ?? {}).length;
    return html`<span
      class="argus-b-pill is-series"
      title=${exceptions
        ? `${entry?.series ?? node.input} - a different series on ${exceptions} of them`
        : (entry?.series ?? node.input)}
      >${entry?.series ?? node.input}${exceptions ? html`<sup>+${exceptions}</sup>` : nothing}</span
    >`;
  }
  if ('param' in node) {
    const entry = doc.params[node.param];
    const exceptions = Object.keys(entry?.per_entity ?? {}).length;
    return html`<span
      class="argus-b-pill is-num"
      title=${entry?.label ?? (exceptions ? `Different on ${exceptions} of them` : nothing)}
      >${entry ? numberWord(entry.value) : node.param}${entry?.unit
        ? html` <span class="argus-b-unit">${entry.unit}</span>`
        : nothing}${exceptions ? html`<sup>+${exceptions}</sup>` : nothing}</span
    >`;
  }
  if ('const' in node) return html`<span class="argus-b-pill is-num">${numberWord(node.const)}</span>`;
  if ('ref' in node) {
    return hex('def', html`${doc.define[node.ref]?.label ?? node.ref}`, not);
  }

  const args = node.args;
  const op = node.op;
  if (op in COMPARE_WORDS) {
    return hex(
      'cmp',
      html`${renderArg(args[0] as ArgusTreeNode, doc)}${word(COMPARE_WORDS[op] as string)}${renderArg(
        args[1] as ArgusTreeNode,
        doc,
      )}`,
      not,
    );
  }
  if (op === 'between' || op === 'outside') {
    return hex(
      'cmp',
      html`${renderArg(args[0] as ArgusTreeNode, doc)}${word(op)}${renderArg(
        args[1] as ArgusTreeNode,
        doc,
      )}${word(op === 'between' ? 'and' : 'to')}${renderArg(args[2] as ArgusTreeNode, doc)}`,
      not,
    );
  }
  const kind = groupKind(node);
  if (kind) {
    // NONE is stored as NOT over an ANY, NOT ALL as NOT over an ALL; their
    // conditions are the inner group's.
    const inner = op === 'not' ? (args[0] as ArgusTreeNode | undefined) : undefined;
    const children =
      op === 'at_least'
        ? args.slice(1)
        : inner && 'op' in inner && (inner.op === 'or' || inner.op === 'and')
          ? inner.args
          : args;
    const words = groupWords(kind, children.length);
    const head = kind === 'at_least' ? `AT LEAST ${String(args[0])}` : words.head;
    const join = words.join;
    return html`
      <div class="argus-b-group is-${kind} ${root ? 'is-root' : 'is-nested'}">
        <div class="argus-b-group-head">
          <span class="argus-b-pill is-logic">${head}</span>${word(words.tail)}
        </div>
        ${children.map(
          (child, i) => html`${i > 0 && join ? html`<span class="argus-b-join" aria-hidden="true">${join}</span>` : nothing}
            <div>${renderArg(child as ArgusTreeNode, doc)}</div>`,
        )}
      </div>
    `;
  }
  if (op === 'valid') {
    return hex('quality', html`${renderArg(args[0] as ArgusTreeNode, doc)}${word('has a reading')}`, not);
  }
  if (op === 'stale') {
    return hex(
      'quality',
      html`${renderArg(args[0] as ArgusTreeNode, doc)}${word('no reading for')}${renderArg(
        args[1] as string,
        doc,
      )}`,
      not,
    );
  }
  if (op in WINDOW_WORDS) {
    return round(
      html`${renderArg(args[1] as string, doc)}${word(WINDOW_WORDS[op] as string)}${word(
        'of',
      )}${renderArg(args[0] as ArgusTreeNode, doc)}`,
    );
  }
  if (ARITHMETIC.has(op) && args.length === 2) {
    return round(
      html`${renderArg(args[0] as ArgusTreeNode, doc)}${word(op)}${renderArg(
        args[1] as ArgusTreeNode,
        doc,
      )}`,
    );
  }
  // A name this file does not know yet: drawn plainly, never dropped.
  const body = html`${word(FUNCTION_WORDS[op] ?? op)}${args.map((a) => renderArg(a, doc))}`;
  return CONDITIONS.has(op) ? hex('cmp', body, not) : round(body);
}

function renderTiming(doc: ArgusTreeDocument): TemplateResult {
  const shape = doc.shape;
  if (shape.kind === 'sustained') {
    return html`${word('for at least')}<span class="argus-b-pill is-time"
        >${durationWords(shape.dwell)}</span
      >`;
  }
  if (shape.kind === 'n_of_m') {
    return html`${word('in')}<span class="argus-b-pill is-num">${shape.n}</span>${word(
        'of the last',
      )}<span class="argus-b-pill is-num">${shape.m}</span
      ><span class="argus-b-pill is-time">${durationWords(shape.every)}</span>${word('windows')}`;
  }
  if (shape.kind === 'silence') return word('as soon as it happens');
  return word('on any single reading');
}

/** The scope as the rule names it, for when the server could not put it in words. */
function scopeFallback(doc: ArgusTreeDocument): string {
  const scope = doc.scope;
  if ('entities' in scope) return scope.entities.join(', ');
  const skipped = scope.except?.length ? ` except ${scope.except.join(', ')}` : '';
  if ('tags' in scope) return `everything tagged ${scope.tags.join(' + ')}${skipped}`;
  return `everything${skipped}`;
}

/**
 * The clause labels, shared by this view and the editor: a short question,
 * and a dimmed line under it that says what the clause does. Chosen 2026-10-05
 * (`docs/design/sketches/argus-rule-labels.html`). When? and What? became one
 * Conditions clause on 2026-10-07: a gate is shown, and saved, as more AND
 * items (`foldGate`).
 */
export const CLAUSES = {
  watch: { label: 'Ships?', hint: 'The ships this rule checks.' },
  alert: { label: 'Conditions', hint: 'If this is true, you get an alert.' },
  timing: { label: 'How long?', hint: 'It must stay true this long first.' },
  severity: { label: 'Severity', hint: 'Info: log only. Watch: this week. Critical: today.' },
  signals: { label: 'Custom signals', hint: 'Your own values, made from signals with a formula.' },
  where: { label: 'Variables', hint: 'A short name for a condition. Use it above.' },
} as const;

export type ClauseKey = keyof typeof CLAUSES;

/** Backtest asks a question, not for an alert: the lines that say otherwise change. */
export const QUICK_HINTS: Partial<Record<ClauseKey, string>> = {
  watch: 'The ships to look at.',
  alert: 'What you are looking for.',
  timing: 'It must stay true this long to count.',
};

/** One clause row: the question and its dimmed line on the left, the blocks on the right. */
export function clause(
  key: ClauseKey,
  kind: string,
  body: TemplateResult,
  quick = false,
  extra: TemplateResult | typeof nothing = nothing,
): TemplateResult {
  const { label } = CLAUSES[key];
  const hint = (quick && QUICK_HINTS[key]) || CLAUSES[key].hint;
  return html`
    <div class="argus-b-clause is-${kind}">
      <div class="argus-b-head">
        <div class="argus-b-label">${label}</div>
        <div class="argus-b-hint">${hint}</div>
        ${extra}
      </div>
      <div class="argus-b-slot">${body}</div>
    </div>
  `;
}

@customElement('argus-rule-blocks')
export class ArgusRuleBlocks extends LightElement {
  @property({ attribute: false }) tree: ArgusRuleTree | null = null;

  override render(): TemplateResult | typeof nothing {
    const tree = this.tree;
    if (!tree) return nothing;
    const doc = tree.document;
    const count = tree.entity_count;
    const noun = tree.entity_noun ?? 'entity';
    const shape = doc.shape;
    // Custom signals (numbers) and named conditions (Variables), apart - as in the editor.
    const signalSet = new Set(signalKeys({ ...doc, define: doc.define ?? {} }));
    const signals = Object.entries(doc.define ?? {}).filter(([key]) => signalSet.has(key));
    const defines = Object.entries(doc.define ?? {})
      .filter(([key]) => !signalSet.has(key))
      .map(([, d]) => d);

    // One Conditions clause: a gate is drawn as more AND items, as the editor
    // shows it; a silence rule keeps its gate as "only while".
    const folded = foldGate(doc);
    const alert =
      shape.kind === 'silence'
        ? html`${hex(
            'quality',
            html`${word('no reading of')}${renderNode({ input: shape.input }, doc)}${word(
              'for',
            )}<span class="argus-b-pill is-time">${durationWords(shape.after)}</span>`,
          )}${doc.gate ? html`${word('only while')}${renderNode(doc.gate, doc, true)}` : nothing}`
        : folded.condition
          ? renderNode(folded.condition, doc, true)
          : word('nothing yet');

    return html`
      <div class="argus-b-canvas">
        <div class="argus-b-stack">
          ${clause(
            'watch',
            'def',
            html`<span class="argus-b-pill is-scope">${tree.scope_words ?? scopeFallback(doc)}</span>${count !=
              null
                ? html`<span class="argus-b-note"
                    >${count} ${noun}${count === 1 ? '' : 's'} today</span
                  >`
                : nothing}`,
          )}
          ${clause('alert', 'logic', alert, false, shape.kind === 'silence' ? nothing : logicKey(folded.condition))}
          ${clause('timing', 'time', renderTiming(doc))}
          ${clause(
            'severity',
            'logic',
            html`<span class="argus-chip is-${doc.severity}">${doc.severity}</span>`,
          )}
        </div>
        ${signals.length
          ? html`<div class="argus-b-stack">
              ${clause(
                'signals',
                'signal',
                html`${signals.map(
                  ([, d]) =>
                    html`<div class="argus-b-define is-signal">
                      <span class="argus-b-pill is-series is-signal">${d.label}</span>${word('is')}<code class="argus-e-formula">${formulaOf(d.expr, doc) ?? 'a computed value'}</code>
                    </div>`,
                )}`,
              )}
            </div>`
          : nothing}
        ${defines.length
          ? html`<div class="argus-b-stack">
              ${clause(
                'where',
                'def',
                html`<div class="argus-b-defines">${defines.map(
                  (d) =>
                    html`<div class="argus-b-define">
                      ${hex('def', html`${d.label}`)}${word('means')}${renderNode(d.expr, doc)}
                    </div>`,
                )}</div>`,
              )}
            </div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-rule-blocks': ArgusRuleBlocks;
  }
}
